import React from 'react';
import { formatPlaceName } from '@/lib/utils';
import { motion } from 'framer-motion';
import { GrowthChartDataItem } from '@/hooks/useGrowthChartData';

interface StackUpAnalysisProps {
  selectedPlace: {
    Place: string;
    place_fips: string;
  } | null;
  growthChartData: GrowthChartDataItem[];
  loading: boolean;
}

const StackUpAnalysis: React.FC<StackUpAnalysisProps> = ({ selectedPlace, growthChartData, loading }) => {

  const getAnalysisText = () => {
    if (!growthChartData || growthChartData.length === 0) {
      return "Awaiting data for analysis.";
    }
    
    const dataItem = growthChartData[0];

    if (dataItem.prevPopGrowth === undefined || dataItem.prevHousingGrowth === undefined) {
      return "Insufficient data for year-over-year analysis.";
    }

    const isLatestCovered = dataItem.housingGrowth >= dataItem.popGrowth;
    const isPreviousCovered = dataItem.prevHousingGrowth >= dataItem.prevPopGrowth;

    const placeName = formatPlaceName(selectedPlace?.Place || '');

    if (isPreviousCovered && !isLatestCovered) {
      return `Conditions in ${placeName} are worsening. Last year, the city's housing supply kept pace with population growth, but it has since fallen behind. It is crucial to address permitting bottlenecks to meet the community's growing needs.`;
    }

    if (!isPreviousCovered && isLatestCovered) {
      return `${placeName} is ahead of the curve. After previously lagging, its housing growth is now outpacing population growth. Now is the perfect time to upgrade permitting tech and upskill staff to solidify this progress for the future.`;
    }
    
    const latestGap = dataItem.popGrowth - dataItem.housingGrowth;
    const previousGap = dataItem.prevPopGrowth - dataItem.prevHousingGrowth;

    if (latestGap > previousGap) {
        if (isLatestCovered) {
             return `${placeName} continues to successfully meet housing demand. However, the margin is shrinking. Diligent planning for the community's future is key to avoid becoming stressed.`;
        }
        return `While the housing situation in ${placeName} hasn't changed status, the gap between population growth and housing supply is widening. Conditions may become stressed soon if this trend continues.`;
    } else {
        if (isLatestCovered) {
            return `${placeName} continues to successfully meet housing demand. Continued planning for the community's future will ensure this success is maintained.`;
        }
        return `${placeName} is making progress in closing the housing gap, but its community is still under stress. Continuing to improve permitting efficiency is key.`;
    }
  };

  const renderContent = () => {
    if (loading) {
      return <div className="flex-grow flex items-center justify-center text-slate-500">Loading...</div>;
    }

    const analysisText = getAnalysisText();

    return (
      <div className="flex flex-col h-full">
        <h3 className="font-semibold text-slate-900 text-base mb-4">
            Is <span className="font-bold">{formatPlaceName(selectedPlace?.Place || '')}</span> permitting for the future?
        </h3>
        <p className="text-sm text-slate-700 leading-relaxed">
          {analysisText}
        </p>
      </div>
    );
  };

  return (
    <motion.div 
      className="bg-white/70 backdrop-blur-sm p-4 rounded-xl border border-slate-200/50 min-h-[400px] flex flex-col h-full"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.4 }}
    >
      {renderContent()}
    </motion.div>
  );
};

export default StackUpAnalysis; 